import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { formatCurrency, formatPercentage, formatNumber, getWeekNumber } from '../utils/helpers';
import AIKeyRequiredDialog from '../components/AIKeyRequiredDialog';
import {
  TrendingUp, TrendingDown, Users, Wallet, Target, CheckCircle2,
  AlertTriangle, Calendar, Sparkles, Loader2, ArrowRight, Clock,
  AlertCircle, FileText, Plus, ChevronRight, Activity, BarChart3,
  Bell, X, Check, Settings, Gauge, DollarSign, Percent, XCircle,
  ShoppingCart, Star, Utensils, PartyPopper, Lightbulb, Building2,
  Euro, UserPlus, MessageSquare, Zap, Eye, Heart, Edit, Trash2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie
} from 'recharts';

const COLORS = ['#C2410C', '#1A2E05', '#D97706', '#0891B2', '#7C3AED', '#DB2777'];

const DashboardPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiInsights, setAiInsights] = useState(null);
  const [showAIKeyDialog, setShowAIKeyDialog] = useState(false);
  
  // Data states
  const [revenue, setRevenue] = useState([]);
  const [costs, setCosts] = useState([]);
  const [goals, setGoals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [arrangements, setArrangements] = useState([]);
  const [events, setEvents] = useState([]);
  const [marketingPlan, setMarketingPlan] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [reviewRequests, setReviewRequests] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [businessSettings, setBusinessSettings] = useState(null);
  const [signals, setSignals] = useState([]);
  const [notificationOpen, setNotificationOpen] = useState(false);
  
  // Quick action dialogs
  const [revenueDialogOpen, setRevenueDialogOpen] = useState(false);
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [hoursDialogOpen, setHoursDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [arrangementDialogOpen, setArrangementDialogOpen] = useState(false);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [ideaDialogOpen, setIdeaDialogOpen] = useState(false);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [calendarDayDialogOpen, setCalendarDayDialogOpen] = useState(false);
  const [selectedCalendarDay, setSelectedCalendarDay] = useState(null);
  const [editEventDialogOpen, setEditEventDialogOpen] = useState(false);
  const [editArrangementDialogOpen, setEditArrangementDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedArrangement, setSelectedArrangement] = useState(null);
  
  // Quick forms
  const [revenueForm, setRevenueForm] = useState({ date: new Date().toISOString().split('T')[0], amount: '', category: 'food', description: '' });
  const [costForm, setCostForm] = useState({ date: new Date().toISOString().split('T')[0], amount: '', category: 'food', description: '' });
  const [hoursForm, setHoursForm] = useState({ employee_id: '', date: new Date().toISOString().split('T')[0], hours: '', type: 'gewerkt' });
  const [reviewForm, setReviewForm] = useState({ guest_name: '', email: '', phone: '' });
  const [arrangementForm, setArrangementForm] = useState({ template_id: '', guest_name: '', guest_count: '', date: '', time: '' });
  const [eventForm, setEventForm] = useState({ name: '', event_date: '', start_time: '', expected_guests: '', description: '' });
  const [actionForm, setActionForm] = useState({ title: '', description: '', priority: 'medium' });
  const [ideaForm, setIdeaForm] = useState({ title: '', description: '', category: 'algemeen', priority: 'normaal' });
  const [companyForm, setCompanyForm] = useState({ 
    name: '', category: 'concurrent', website: '', city: '', notes: '',
    instagram_url: '', facebook_url: '', linkedin_url: '', tiktok_url: ''
  });
  const [shiftForm, setShiftForm] = useState({ employee_id: '', date: new Date().toISOString().split('T')[0], start_time: '09:00', end_time: '17:00', role: '' });

  // Calendar state
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const currentYear = new Date().getFullYear();

  // Fetch all data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [
        revenueRes, costsRes, goalsRes, tasksRes, employeesRes,
        arrangementsRes, eventsRes, planRes, campaignsRes, reviewRequestsRes, reviewsRes, settingsRes, signalsRes
      ] = await Promise.all([
        api.get('/revenue'),
        api.get('/costs'),
        api.get('/goals'),
        api.get('/tasks'),
        api.get('/employees'),
        api.get('/arrangements'),
        api.get('/events'),
        api.get(`/marketing-plan/${currentYear}`).catch(() => ({ data: null })),
        api.get('/campaigns').catch(() => ({ data: [] })),
        api.get('/review-requests').catch(() => ({ data: [] })),
        api.get('/guest-feedback').catch(() => ({ data: [] })),
        api.get('/business-settings').catch(() => ({ data: null })),
        api.get('/signals', { params: { is_read: false } }).catch(() => ({ data: [] }))
      ]);
      
      setRevenue(revenueRes.data);
      setCosts(costsRes.data);
      setGoals(goalsRes.data);
      setTasks(tasksRes.data);
      setEmployees(employeesRes.data);
      setArrangements(arrangementsRes.data || []);
      setEvents(eventsRes.data || []);
      setMarketingPlan(planRes.data);
      setCampaigns(campaignsRes.data || []);
      setReviewRequests(reviewRequestsRes.data || []);
      setReviews(reviewsRes.data || []);
      setBusinessSettings(settingsRes.data);
      setSignals(signalsRes.data || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate KPIs
  const today = new Date();
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - today.getDay() + 1);
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

  const weekRevenue = revenue
    .filter(r => new Date(r.date) >= weekStart)
    .reduce((sum, r) => sum + r.amount, 0);
  const monthRevenue = revenue
    .filter(r => new Date(r.date) >= monthStart)
    .reduce((sum, r) => sum + r.amount, 0);
  const totalRevenue = revenue.reduce((sum, r) => sum + r.amount, 0);

  const weekCosts = costs
    .filter(c => new Date(c.date) >= weekStart)
    .reduce((sum, c) => sum + (c.amount || c.food_cost || 0) + (c.beverage_cost || 0) + (c.personnel_cost || 0) + (c.other_costs || 0), 0);
  const monthCosts = costs
    .filter(c => new Date(c.date) >= monthStart)
    .reduce((sum, c) => sum + (c.amount || c.food_cost || 0) + (c.beverage_cost || 0) + (c.personnel_cost || 0) + (c.other_costs || 0), 0);

  const profitMargin = monthRevenue > 0 ? ((monthRevenue - monthCosts) / monthRevenue * 100) : 0;
  const activeGoals = goals.filter(g => g.status === 'bezig').length;
  const completedGoals = goals.filter(g => g.status === 'voltooid').length;
  const pendingTasks = tasks.filter(t => t.status !== 'done').length;
  const upcomingEvents = events.filter(e => new Date(e.event_date) >= today).length;
  const upcomingArrangements = arrangements.filter(a => !a.is_template && a.status === 'geboekt').length;

  // Quick action handlers
  const handleQuickRevenue = async () => {
    try {
      await api.post('/revenue', { ...revenueForm, amount: parseFloat(revenueForm.amount) });
      toast.success('Omzet toegevoegd!');
      setRevenueDialogOpen(false);
      setRevenueForm({ date: new Date().toISOString().split('T')[0], amount: '', category: 'food', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij toevoegen omzet');
    }
  };

  const handleQuickCost = async () => {
    if (!costForm.amount) {
      toast.error('Vul een bedrag in');
      return;
    }
    try {
      await api.post('/costs/quick', null, {
        params: {
          date: costForm.date,
          amount: parseFloat(costForm.amount),
          category: costForm.category,
          description: costForm.description
        }
      });
      toast.success('Inkoop toegevoegd!');
      setCostDialogOpen(false);
      setCostForm({ date: new Date().toISOString().split('T')[0], amount: '', category: 'food', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij toevoegen inkoop');
    }
  };

  const handleQuickHours = async () => {
    try {
      await api.post('/work-hours', { ...hoursForm, hours: parseFloat(hoursForm.hours) });
      toast.success('Uren toegevoegd!');
      setHoursDialogOpen(false);
      setHoursForm({ employee_id: '', date: new Date().toISOString().split('T')[0], hours: '', type: 'gewerkt' });
    } catch (error) {
      toast.error('Fout bij toevoegen uren');
    }
  };

  const handleQuickReview = async () => {
    try {
      await api.post('/reviews/request', reviewForm);
      toast.success('Review verzoek verstuurd!');
      setReviewDialogOpen(false);
      setReviewForm({ guest_name: '', email: '', phone: '' });
    } catch (error) {
      toast.error('Fout bij versturen review verzoek');
    }
  };

  const handleQuickArrangement = async () => {
    if (!arrangementForm.guest_name) {
      toast.error('Vul een gastnaam in');
      return;
    }
    try {
      await api.post('/arrangements/quick-book', null, {
        params: {
          template_id: arrangementForm.template_id || '',
          guest_name: arrangementForm.guest_name,
          guest_count: parseInt(arrangementForm.guest_count) || 0,
          date: arrangementForm.date,
          time: arrangementForm.time
        }
      });
      toast.success('Arrangement geboekt!');
      setArrangementDialogOpen(false);
      setArrangementForm({ template_id: '', guest_name: '', guest_count: '', date: '', time: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij boeken arrangement');
    }
  };

  const handleQuickEvent = async () => {
    if (!eventForm.name) {
      toast.error('Vul een eventnaam in');
      return;
    }
    try {
      await api.post('/events/quick', null, {
        params: {
          name: eventForm.name,
          event_date: eventForm.event_date,
          start_time: eventForm.start_time || '',
          expected_guests: parseInt(eventForm.expected_guests) || 0,
          description: eventForm.description || ''
        }
      });
      toast.success('Event aangemaakt!');
      setEventDialogOpen(false);
      setEventForm({ name: '', event_date: '', start_time: '', expected_guests: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij aanmaken event');
    }
  };

  const handleQuickAction = async () => {
    try {
      await api.post('/signals', { ...actionForm, type: 'actie', is_read: false });
      toast.success('Actie toegevoegd!');
      setActionDialogOpen(false);
      setActionForm({ title: '', description: '', priority: 'medium' });
    } catch (error) {
      toast.error('Fout bij toevoegen actie');
    }
  };

  const handleQuickIdea = async () => {
    if (!ideaForm.title.trim()) {
      toast.error('Vul een titel in');
      return;
    }
    try {
      await api.post('/ideas-inbox', ideaForm);
      toast.success('Idee toegevoegd aan Inbox!');
      setIdeaDialogOpen(false);
      setIdeaForm({ title: '', description: '', category: 'algemeen', priority: 'normaal' });
    } catch (error) {
      toast.error('Fout bij toevoegen idee');
    }
  };

  const handleAddCompany = async () => {
    if (!companyForm.name.trim()) {
      toast.error('Vul een naam in');
      return;
    }
    try {
      await api.post('/research/companies', {
        name: companyForm.name,
        category: companyForm.category,
        website: companyForm.website,
        city: companyForm.city,
        notes: companyForm.notes,
        social_media: {
          instagram: companyForm.instagram_url,
          facebook: companyForm.facebook_url,
          linkedin: companyForm.linkedin_url,
          tiktok: companyForm.tiktok_url
        }
      });
      toast.success('Bedrijf toegevoegd aan Research!');
      setCompanyDialogOpen(false);
      setCompanyForm({ 
        name: '', category: 'concurrent', website: '', city: '', notes: '',
        instagram_url: '', facebook_url: '', linkedin_url: '', tiktok_url: ''
      });
    } catch (error) {
      toast.error('Fout bij toevoegen bedrijf');
    }
  };

  const handleQuickShift = async () => {
    if (!shiftForm.employee_id) {
      toast.error('Selecteer een medewerker');
      return;
    }
    try {
      await api.post('/schedules', null, {
        params: {
          employee_id: shiftForm.employee_id,
          date: shiftForm.date,
          shift_start: shiftForm.start_time,
          shift_end: shiftForm.end_time
        }
      });
      toast.success('Dienst ingepland!');
      setShiftDialogOpen(false);
      setShiftForm({ employee_id: '', date: new Date().toISOString().split('T')[0], start_time: '09:00', end_time: '17:00', role: '' });
    } catch (error) {
      toast.error('Fout bij inplannen dienst');
    }
  };

  const openCalendarDayDialog = (dayNum, dateStr, dayEvents, dayCampaigns, daySeasons) => {
    setSelectedCalendarDay({
      dayNum,
      dateStr,
      events: dayEvents,
      campaigns: dayCampaigns,
      seasons: daySeasons
    });
    setCalendarDayDialogOpen(true);
  };

  const openEditEvent = (event) => {
    setSelectedEvent(event);
    setEventForm({
      name: event.name || '',
      event_date: event.event_date || '',
      start_time: event.start_time || '',
      expected_guests: event.expected_guests || '',
      description: event.description || ''
    });
    setEditEventDialogOpen(true);
  };

  const handleUpdateEvent = async () => {
    try {
      await api.put(`/events/${selectedEvent.id}`, eventForm);
      toast.success('Event bijgewerkt');
      setEditEventDialogOpen(false);
      setSelectedEvent(null);
      fetchData();
    } catch (error) {
      toast.error('Fout bij bijwerken');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Event verwijderen?')) return;
    try {
      await api.delete(`/events/${eventId}`);
      toast.success('Event verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const openEditArrangement = (arr) => {
    setSelectedArrangement(arr);
    setArrangementForm({
      template_id: arr.template_id || '',
      guest_name: arr.guest_name || '',
      guest_count: arr.guests || '',
      date: arr.date || '',
      time: arr.time || ''
    });
    setEditArrangementDialogOpen(true);
  };

  const handleUpdateArrangement = async () => {
    try {
      await api.put(`/arrangements/${selectedArrangement.id}`, {
        guest_name: arrangementForm.guest_name,
        guests: parseInt(arrangementForm.guest_count) || 0,
        date: arrangementForm.date,
        time: arrangementForm.time
      });
      toast.success('Arrangement bijgewerkt');
      setEditArrangementDialogOpen(false);
      setSelectedArrangement(null);
      fetchData();
    } catch (error) {
      toast.error('Fout bij bijwerken');
    }
  };

  // Mark signal as read and navigate
  const handleSignalClick = async (signal) => {
    try {
      await api.put(`/signals/${signal.id}/read`);
      setSignals(prev => prev.filter(s => s.id !== signal.id));
      setNotificationOpen(false);
      
      // Navigate based on signal type
      if (signal.type === 'taak' || signal.type === 'actie' || signal.type === 'doel_achterstand') {
        navigate('/company/doelen-structuur');
      } else if (signal.type === 'review') {
        navigate('/company/gast-beleving');
      } else if (signal.type === 'kosten_stijging' || signal.type === 'budget') {
        navigate('/company/kosten-marges');
      } else if (signal.type === 'omzet_stijging' || signal.type === 'omzet_daling') {
        navigate('/company/omzet-verkoop');
      } else {
        navigate('/company/acties-evaluatie');
      }
    } catch (error) {
      console.error('Error marking signal as read:', error);
    }
  };

  // Mark all signals as read
  const handleMarkAllRead = async () => {
    try {
      await Promise.all(signals.map(s => api.put(`/signals/${s.id}/read`)));
      setSignals([]);
      toast.success('Alle notificaties gelezen');
    } catch (error) {
      toast.error('Fout bij markeren als gelezen');
    }
  };

  const generateAIInsights = async () => {
    setAiLoading(true);
    try {
      const res = await api.post('/ai/dashboard-insights', {
        revenue: { week: weekRevenue, month: monthRevenue },
        costs: { week: weekCosts, month: monthCosts },
        goals: { active: activeGoals, completed: completedGoals },
        tasks_pending: pendingTasks
      });
      setAiInsights(res.data.insights);
    } catch (error) {
      // Check if error is due to missing API key
      if (error.response?.data?.detail === 'NO_API_KEY') {
        setShowAIKeyDialog(true);
      } else {
        toast.error('Fout bij genereren AI inzichten');
      }
    } finally {
      setAiLoading(false);
    }
  };

  // Get arrangement templates
  const arrangementTemplates = arrangements.filter(a => a.is_template);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
            Dashboard
          </h1>
          <p className="text-sm md:text-base text-[#57534E]">
            Welkom terug, {user?.name || 'Gebruiker'}! • Week {getWeekNumber(today)}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Notification Bell with Popover */}
          <Popover open={notificationOpen} onOpenChange={setNotificationOpen}>
            <PopoverTrigger asChild>
              <Button 
                variant="outline"
                size="icon"
                className="relative h-10 w-10 rounded-full"
                data-testid="notification-bell"
              >
                <Bell className="w-5 h-5" />
                {signals.length > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 bg-rose-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                    {signals.length > 9 ? '9+' : signals.length}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end">
              <div className="p-3 border-b bg-stone-50">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sm">Notificaties</h3>
                  {signals.length > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-xs h-7 text-stone-500 hover:text-stone-700"
                      onClick={handleMarkAllRead}
                    >
                      Alles gelezen
                    </Button>
                  )}
                </div>
              </div>
              <ScrollArea className="max-h-80">
                {signals.length === 0 ? (
                  <div className="p-6 text-center text-stone-500">
                    <Bell className="w-10 h-10 mx-auto mb-2 text-stone-300" />
                    <p className="text-sm">Geen nieuwe notificaties</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {signals.slice(0, 10).map((signal) => (
                      <button
                        key={signal.id}
                        onClick={() => handleSignalClick(signal)}
                        className="w-full p-3 text-left hover:bg-stone-50 transition-colors flex items-start gap-3"
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          signal.severity === 'error' || signal.severity === 'warning' ? 'bg-rose-100 text-rose-600' :
                          signal.type === 'review' ? 'bg-amber-100 text-amber-600' :
                          'bg-blue-100 text-blue-600'
                        }`}>
                          {signal.type === 'taak' || signal.type === 'actie' ? (
                            <AlertTriangle className="w-4 h-4" />
                          ) : signal.type === 'review' ? (
                            <Star className="w-4 h-4" />
                          ) : signal.type === 'kosten_stijging' || signal.type === 'omzet_daling' ? (
                            <TrendingDown className="w-4 h-4" />
                          ) : signal.type === 'omzet_stijging' ? (
                            <TrendingUp className="w-4 h-4" />
                          ) : (
                            <Bell className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-stone-900 line-clamp-2">{signal.message}</p>
                          <p className="text-xs text-stone-400 mt-1">
                            {new Date(signal.created_at).toLocaleDateString('nl-NL', { 
                              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
                            })}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-stone-400 flex-shrink-0 mt-1" />
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
              {signals.length > 0 && (
                <div className="p-2 border-t bg-stone-50">
                  <Button 
                    variant="ghost" 
                    className="w-full text-sm text-stone-600 hover:text-stone-900"
                    onClick={() => {
                      setNotificationOpen(false);
                      navigate('/company/acties-evaluatie');
                    }}
                  >
                    Alle notificaties bekijken
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
          
          {/* Settings Button - Only for company owners */}
          {(user?.role === 'company' || user?.role === 'admin') && (
            <Button 
              variant="outline"
              size="icon"
              className="h-10 w-10 rounded-full"
              onClick={() => navigate('/company/instellingen')}
              data-testid="settings-btn"
            >
              <Settings className="w-5 h-5" />
            </Button>
          )}
          
          <Button 
            className="btn-primary w-full sm:w-auto"
            onClick={generateAIInsights}
            disabled={aiLoading}
          >
            {aiLoading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyseren...</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" />AI Analyse</>
            )}
          </Button>
        </div>
      </div>

      {/* AI Insights Banner */}
      {aiInsights && (
        <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-purple-600 mt-0.5" />
              <div>
                <p className="font-semibold text-purple-800">AI Inzicht</p>
                <p className="text-purple-700">{aiInsights}</p>
              </div>
              <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setAiInsights(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ==================== SNELLE ACTIES ==================== */}
      <section>
        <h2 className="text-base md:text-lg font-bold text-[#1C1917] mb-3 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
          <Zap className="w-5 h-5 text-amber-500" />
          Snelle Acties
        </h2>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 md:gap-3">
          {/* Quick Revenue */}
          <Button 
            variant="outline" 
            className="h-16 md:h-20 flex-col gap-1 md:gap-2 hover:bg-emerald-50 hover:border-emerald-400 hover:text-emerald-700 p-2"
            onClick={() => setRevenueDialogOpen(true)}
          >
            <Euro className="w-5 h-5 md:w-6 md:h-6 text-emerald-600" />
            <span className="text-xs md:text-sm">+ Omzet</span>
          </Button>

          {/* Quick Cost */}
          <Button 
            variant="outline" 
            className="h-16 md:h-20 flex-col gap-1 md:gap-2 hover:bg-rose-50 hover:border-rose-400 hover:text-rose-700 p-2"
            onClick={() => setCostDialogOpen(true)}
          >
            <ShoppingCart className="w-5 h-5 md:w-6 md:h-6 text-rose-600" />
            <span className="text-xs md:text-sm">+ Inkoop</span>
          </Button>

          {/* Quick Hours */}
          <Button 
            variant="outline" 
            className="h-16 md:h-20 flex-col gap-1 md:gap-2 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-700 p-2"
            onClick={() => setHoursDialogOpen(true)}
          >
            <Clock className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
            <span className="text-xs md:text-sm">+ Uren</span>
          </Button>

          {/* Quick Shift */}
          <Button 
            variant="outline" 
            className="h-16 md:h-20 flex-col gap-1 md:gap-2 hover:bg-violet-50 hover:border-violet-400 hover:text-violet-700 p-2"
            onClick={() => setShiftDialogOpen(true)}
          >
            <Users className="w-5 h-5 md:w-6 md:h-6 text-violet-600" />
            <span className="text-xs md:text-sm">+ Dienst</span>
          </Button>

          {/* Quick Review */}
          <Button 
            variant="outline" 
            className="h-16 md:h-20 flex-col gap-1 md:gap-2 hover:bg-yellow-50 hover:border-yellow-400 hover:text-yellow-700 p-2"
            onClick={() => setReviewDialogOpen(true)}
          >
            <Star className="w-5 h-5 md:w-6 md:h-6 text-yellow-600" />
            <span className="text-xs md:text-sm">+ Review</span>
          </Button>

          {/* Quick Arrangement */}
          <Button 
            variant="outline" 
            className="h-16 md:h-20 flex-col gap-1 md:gap-2 hover:bg-purple-50 hover:border-purple-400 hover:text-purple-700 p-2"
            onClick={() => setArrangementDialogOpen(true)}
          >
            <Utensils className="w-5 h-5 md:w-6 md:h-6 text-purple-600" />
            <span className="text-xs md:text-sm">+ Arr.</span>
          </Button>

          {/* Quick Event */}
          <Button 
            variant="outline" 
            className="h-16 md:h-20 flex-col gap-1 md:gap-2 hover:bg-pink-50 hover:border-pink-400 hover:text-pink-700 p-2"
            onClick={() => setEventDialogOpen(true)}
          >
            <PartyPopper className="w-5 h-5 md:w-6 md:h-6 text-pink-600" />
            <span className="text-xs md:text-sm">+ Event</span>
          </Button>

          {/* Quick Idea */}
          <Button 
            variant="outline" 
            className="h-16 md:h-20 flex-col gap-1 md:gap-2 hover:bg-cyan-50 hover:border-cyan-400 hover:text-cyan-700 p-2"
            onClick={() => setIdeaDialogOpen(true)}
          >
            <Lightbulb className="w-5 h-5 md:w-6 md:h-6 text-cyan-600" />
            <span className="text-xs md:text-sm">+ Idee</span>
          </Button>

          {/* Add Company for Research */}
          <Button 
            variant="outline" 
            className="h-16 md:h-20 flex-col gap-1 md:gap-2 hover:bg-teal-50 hover:border-teal-400 hover:text-teal-700 p-2"
            onClick={() => setCompanyDialogOpen(true)}
          >
            <Building2 className="w-5 h-5 md:w-6 md:h-6 text-teal-600" />
            <span className="text-xs md:text-sm">+ Bedrijf</span>
          </Button>

          {/* New Marketing Action */}
          <Button 
            variant="outline" 
            className="h-16 md:h-20 flex-col gap-1 md:gap-2 hover:bg-indigo-50 hover:border-indigo-400 hover:text-indigo-700 p-2"
            onClick={() => setActionDialogOpen(true)}
          >
            <Zap className="w-5 h-5 md:w-6 md:h-6 text-indigo-600" />
            <span className="text-xs md:text-sm">+ Actie</span>
          </Button>
        </div>
      </section>

      {/* ==================== KERN KPIs ==================== */}
      <section>
        <h2 className="text-base md:text-lg font-bold text-[#1C1917] mb-3 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
          <BarChart3 className="w-5 h-5 text-[#C2410C]" />
          Kern KPIs
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-4">
          {/* Week Omzet */}
          <Card className="card-base hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/company/omzet-verkoop')}>
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center justify-between mb-1 md:mb-2">
                <span className="text-xs text-[#57534E]">Week Omzet</span>
                <Euro className="w-3 h-3 md:w-4 md:h-4 text-emerald-600" />
              </div>
              <p className="text-lg md:text-2xl font-bold text-emerald-600">{formatCurrency(weekRevenue)}</p>
            </CardContent>
          </Card>

          {/* Maand Omzet */}
          <Card className="card-base hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/company/omzet-verkoop')}>
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center justify-between mb-1 md:mb-2">
                <span className="text-xs text-[#57534E]">Maand Omzet</span>
                <Wallet className="w-3 h-3 md:w-4 md:h-4 text-emerald-600" />
              </div>
              <p className="text-lg md:text-2xl font-bold text-[#1C1917]">{formatCurrency(monthRevenue)}</p>
            </CardContent>
          </Card>

          {/* Maand Kosten */}
          <Card className="card-base hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/company/kosten-marges')}>
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center justify-between mb-1 md:mb-2">
                <span className="text-xs text-[#57534E]">Maand Kosten</span>
                <ShoppingCart className="w-3 h-3 md:w-4 md:h-4 text-rose-600" />
              </div>
              <p className="text-lg md:text-2xl font-bold text-rose-600">{formatCurrency(monthCosts)}</p>
            </CardContent>
          </Card>

          {/* Winstmarge */}
          <Card className="card-base hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/company/kosten-marges')}>
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center justify-between mb-1 md:mb-2">
                <span className="text-xs text-[#57534E]">Winstmarge</span>
                {profitMargin >= 0 ? <TrendingUp className="w-3 h-3 md:w-4 md:h-4 text-emerald-600" /> : <TrendingDown className="w-3 h-3 md:w-4 md:h-4 text-rose-600" />}
              </div>
              <p className={`text-lg md:text-2xl font-bold ${profitMargin >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {profitMargin.toFixed(1)}%
              </p>
            </CardContent>
          </Card>

          {/* Actieve Doelen */}
          <Card className="card-base hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/company/doelen-structuur')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-[#57534E]">Actieve Doelen</span>
                <Target className="w-4 h-4 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-blue-600">{activeGoals}</p>
              <p className="text-xs text-emerald-600">{completedGoals} voltooid</p>
            </CardContent>
          </Card>

          {/* Open Taken */}
          <Card className="card-base hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/company/doelen-structuur')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-[#57534E]">Open Taken</span>
                <CheckCircle2 className="w-4 h-4 text-amber-600" />
              </div>
              <p className="text-2xl font-bold text-amber-600">{pendingTasks}</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ==================== TWEE KOLOMMEN: KALENDER + UPCOMING ==================== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Marketing Kalender */}
        <Card className="card-base">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#C2410C]" />
                Marketing Kalender
              </CardTitle>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => setCalendarMonth(prev => prev > 0 ? prev - 1 : 11)}>←</Button>
                <span className="text-sm font-medium w-16 text-center">
                  {['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'][calendarMonth]}
                </span>
                <Button variant="ghost" size="sm" onClick={() => setCalendarMonth(prev => prev < 11 ? prev + 1 : 0)}>→</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Mini Calendar */}
            <div className="grid grid-cols-7 gap-1 text-xs">
              {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map(day => (
                <div key={day} className="p-1 text-center font-semibold text-[#57534E]">{day}</div>
              ))}
              {Array.from({ length: 35 }, (_, i) => {
                const firstDay = new Date(currentYear, calendarMonth, 1).getDay();
                const daysInMonth = new Date(currentYear, calendarMonth + 1, 0).getDate();
                const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1;
                const dayNum = i - adjustedFirstDay + 1;
                const isValidDay = dayNum > 0 && dayNum <= daysInMonth;
                const dateStr = `${currentYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                const currentMonthNum = calendarMonth + 1;
                
                const dayEvents = events.filter(e => e.event_date === dateStr);
                const dayCampaigns = campaigns.filter(c => c.start_date <= dateStr && (!c.end_date || c.end_date >= dateStr));
                
                // Check seasonal campaigns
                const daySeasons = (marketingPlan?.seasonal_campaigns || []).filter(s => {
                  if (!s.is_planned || !s.suggested_start) return false;
                  const [startDay, startMonth] = s.suggested_start.split('-').map(Number);
                  const [endDay, endMonth] = (s.suggested_end || s.suggested_start).split('-').map(Number);
                  if (startMonth === endMonth) return currentMonthNum === startMonth && dayNum >= startDay && dayNum <= endDay;
                  if (startMonth < endMonth) {
                    if (currentMonthNum === startMonth) return dayNum >= startDay;
                    if (currentMonthNum === endMonth) return dayNum <= endDay;
                    return currentMonthNum > startMonth && currentMonthNum < endMonth;
                  }
                  return false;
                });

                const hasContent = dayEvents.length > 0 || dayCampaigns.length > 0 || daySeasons.length > 0;
                const isToday = dayNum === today.getDate() && calendarMonth === today.getMonth();
                
                return (
                  <div 
                    key={i} 
                    className={`p-1 min-h-[32px] rounded text-center cursor-pointer transition-all ${
                      isValidDay 
                        ? isToday 
                          ? 'bg-[#C2410C] text-white font-bold hover:bg-[#A33A0B]'
                          : hasContent 
                            ? 'bg-amber-100 border border-amber-300 hover:bg-amber-200' 
                            : 'hover:bg-stone-100'
                        : ''
                    }`}
                    onClick={() => isValidDay && openCalendarDayDialog(dayNum, dateStr, dayEvents, dayCampaigns, daySeasons)}
                  >
                    {isValidDay && (
                      <>
                        <span className="text-xs">{dayNum}</span>
                        {hasContent && !isToday && (
                          <div className="flex justify-center gap-0.5 mt-0.5">
                            {daySeasons.length > 0 && <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
                            {dayEvents.length > 0 && <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />}
                            {dayCampaigns.length > 0 && <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Legend */}
            <div className="flex items-center gap-4 mt-3 text-xs text-[#57534E]">
              <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-amber-500" /> Seizoen</div>
              <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-purple-500" /> Event</div>
              <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-emerald-500" /> Campagne</div>
            </div>
          </CardContent>
        </Card>

        {/* Upcoming */}
        <Card className="card-base">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#C2410C]" />
              Binnenkort
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Upcoming Events */}
            {events.filter(e => new Date(e.event_date) >= today).slice(0, 3).map(event => (
              <div 
                key={event.id} 
                className="flex items-center gap-3 p-2 rounded-lg bg-purple-50 border border-purple-200 cursor-pointer hover:bg-purple-100 transition-colors"
                onClick={() => openEditEvent(event)}
              >
                <PartyPopper className="w-5 h-5 text-purple-600" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{event.name}</p>
                  <p className="text-xs text-[#57534E]">{new Date(event.event_date).toLocaleDateString('nl-NL')} • {event.expected_guests || 0} gasten</p>
                </div>
                <Badge variant="outline" className="text-purple-600">Event</Badge>
              </div>
            ))}

            {/* Upcoming Arrangements */}
            {arrangements.filter(a => !a.is_template && a.status === 'geboekt' && new Date(a.date) >= today).slice(0, 3).map(arr => (
              <div 
                key={arr.id} 
                className="flex items-center gap-3 p-2 rounded-lg bg-emerald-50 border border-emerald-200 cursor-pointer hover:bg-emerald-100 transition-colors"
                onClick={() => openEditArrangement(arr)}
              >
                <Utensils className="w-5 h-5 text-emerald-600" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{arr.guest_name || 'Arrangement'}</p>
                  <p className="text-xs text-[#57534E]">{new Date(arr.date).toLocaleDateString('nl-NL')} • {arr.guests || 0} personen</p>
                </div>
                <Badge variant="outline" className="text-emerald-600">Boeking</Badge>
              </div>
            ))}

            {/* Active Goals Progress */}
            {goals.filter(g => g.status === 'bezig').slice(0, 2).map(goal => (
              <div 
                key={goal.id} 
                className="p-2 rounded-lg bg-blue-50 border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
                onClick={() => navigate('/company/doelen-structuur')}
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="font-medium text-sm truncate">{goal.title}</p>
                  <span className="text-xs text-blue-600">{goal.progress?.toFixed(0) || 0}%</span>
                </div>
                <Progress value={goal.progress || 0} className="h-1.5" />
              </div>
            ))}

            {events.filter(e => new Date(e.event_date) >= today).length === 0 && 
             arrangements.filter(a => !a.is_template && a.status === 'geboekt').length === 0 &&
             goals.filter(g => g.status === 'bezig').length === 0 && (
              <div className="text-center py-6 text-[#57534E]">
                <Calendar className="w-10 h-10 mx-auto mb-2 text-stone-300" />
                <p className="text-sm">Geen komende events of boekingen</p>
                <Button variant="link" className="text-[#C2410C]" onClick={() => setEventDialogOpen(true)}>
                  + Event aanmaken
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ==================== GAST & BELEVING + URGENTE TAKEN ==================== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gast & Beleving */}
        <Card className="card-base">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Heart className="w-5 h-5 text-rose-500" />
                Gast & Beleving
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/company/gast-beleving')}>
                Bekijk alles <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Google Rating + Review Stats */}
            <div className="grid grid-cols-4 gap-3 mb-4">
              {/* Google Rating */}
              <div className="p-2 bg-yellow-50 rounded-lg text-center border border-yellow-200">
                <div className="flex items-center justify-center gap-1">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <p className="text-xl font-bold text-yellow-600">
                    {businessSettings?.google_rating || '-'}
                  </p>
                </div>
                <p className="text-xs text-[#57534E]">Google</p>
              </div>
              <div className="p-2 bg-amber-50 rounded-lg text-center">
                <p className="text-xl font-bold text-amber-600">{reviewRequests.filter(r => r.status === 'concept').length}</p>
                <p className="text-xs text-[#57534E]">Concept</p>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg text-center">
                <p className="text-xl font-bold text-blue-600">{reviewRequests.filter(r => r.status === 'verzonden').length}</p>
                <p className="text-xs text-[#57534E]">Verzonden</p>
              </div>
              <div className="p-2 bg-emerald-50 rounded-lg text-center">
                <p className="text-xl font-bold text-emerald-600">{reviewRequests.filter(r => r.review_received).length}</p>
                <p className="text-xs text-[#57534E]">Ontvangen</p>
              </div>
            </div>

            {/* Recent Review Requests */}
            {reviewRequests.slice(0, 3).map(req => (
              <div 
                key={req.id} 
                className="flex items-center gap-3 p-2 rounded-lg bg-stone-50 border cursor-pointer hover:bg-stone-100 transition-colors"
                onClick={() => navigate('/company/gast-beleving')}
              >
                <Star className={`w-5 h-5 ${req.review_received ? 'text-emerald-500' : 'text-amber-500'}`} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{req.guest_name}</p>
                  <p className="text-xs text-[#57534E]">{req.guest_email}</p>
                </div>
                <Badge className={
                  req.status === 'verzonden' ? 'bg-blue-100 text-blue-700' :
                  req.review_received ? 'bg-emerald-100 text-emerald-700' :
                  'bg-stone-100 text-stone-600'
                }>
                  {req.review_received ? 'Ontvangen' : req.status === 'verzonden' ? 'Verzonden' : 'Concept'}
                </Badge>
              </div>
            ))}

            {reviewRequests.length === 0 && (
              <div className="text-center py-4 text-[#57534E]">
                <Star className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                <p className="text-sm">Nog geen review verzoeken</p>
                <Button variant="link" className="text-[#C2410C]" onClick={() => setReviewDialogOpen(true)}>
                  + Verzoek versturen
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Urgente Taken */}
        <Card className="card-base">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-rose-500" />
                Urgente Taken
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/company/doelen-structuur')}>
                Alle taken <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* High priority tasks */}
            {tasks
              .filter(t => t.status === 'open' && (t.priority === 'hoog' || t.priority === 'high'))
              .slice(0, 5)
              .map(task => (
                <div 
                  key={task.id} 
                  className="flex items-center gap-3 p-2 rounded-lg bg-rose-50 border border-rose-200 cursor-pointer hover:bg-rose-100 transition-colors"
                  onClick={() => navigate('/company/doelen-structuur')}
                >
                  <AlertCircle className="w-5 h-5 text-rose-600" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{task.title}</p>
                    {task.due_date && (
                      <p className="text-xs text-rose-600">
                        Deadline: {new Date(task.due_date).toLocaleDateString('nl-NL')}
                      </p>
                    )}
                  </div>
                  <Badge className="bg-rose-100 text-rose-700">Urgent</Badge>
                </div>
              ))}

            {/* Overdue tasks */}
            {tasks
              .filter(t => t.status === 'open' && t.due_date && new Date(t.due_date) < today && t.priority !== 'hoog' && t.priority !== 'high')
              .slice(0, 3)
              .map(task => (
                <div 
                  key={task.id} 
                  className="flex items-center gap-3 p-2 rounded-lg bg-amber-50 border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors"
                  onClick={() => navigate('/company/doelen-structuur')}
                >
                  <Clock className="w-5 h-5 text-amber-600" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{task.title}</p>
                    <p className="text-xs text-amber-600">
                      Verlopen: {new Date(task.due_date).toLocaleDateString('nl-NL')}
                    </p>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700">Te laat</Badge>
                </div>
              ))}

            {tasks.filter(t => t.status === 'open' && (t.priority === 'hoog' || t.priority === 'high' || (t.due_date && new Date(t.due_date) < today))).length === 0 && (
              <div className="text-center py-6 text-[#57534E]">
                <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
                <p className="text-sm font-medium text-emerald-600">Geen urgente taken!</p>
                <p className="text-xs">Je bent helemaal bij</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ==================== DIALOGS ==================== */}
      
      {/* Revenue Dialog */}
      <Dialog open={revenueDialogOpen} onOpenChange={setRevenueDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Snel Omzet Toevoegen</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Datum</Label>
                <Input type="date" value={revenueForm.date} onChange={(e) => setRevenueForm({...revenueForm, date: e.target.value})} />
              </div>
              <div>
                <Label>Bedrag (€)</Label>
                <Input type="number" placeholder="0.00" value={revenueForm.amount} onChange={(e) => setRevenueForm({...revenueForm, amount: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Categorie</Label>
              <Select value={revenueForm.category} onValueChange={(v) => setRevenueForm({...revenueForm, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="food">Food</SelectItem>
                  <SelectItem value="beverage">Dranken</SelectItem>
                  <SelectItem value="arrangement">Arrangementen</SelectItem>
                  <SelectItem value="event">Events</SelectItem>
                  <SelectItem value="other">Overig</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Beschrijving (optioneel)</Label>
              <Input placeholder="bijv. Lunch service" value={revenueForm.description} onChange={(e) => setRevenueForm({...revenueForm, description: e.target.value})} />
            </div>
            <Button className="w-full btn-primary" onClick={handleQuickRevenue}>
              <Plus className="w-4 h-4 mr-2" />Toevoegen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cost Dialog */}
      <Dialog open={costDialogOpen} onOpenChange={setCostDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Snel Inkoop Toevoegen</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Datum</Label>
                <Input type="date" value={costForm.date} onChange={(e) => setCostForm({...costForm, date: e.target.value})} />
              </div>
              <div>
                <Label>Bedrag (€)</Label>
                <Input type="number" placeholder="0.00" value={costForm.amount} onChange={(e) => setCostForm({...costForm, amount: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Categorie</Label>
              <Select value={costForm.category} onValueChange={(v) => setCostForm({...costForm, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="food">Food inkoop</SelectItem>
                  <SelectItem value="beverage">Dranken inkoop</SelectItem>
                  <SelectItem value="personnel">Personeel</SelectItem>
                  <SelectItem value="utilities">Utilities</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="other">Overig</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Beschrijving (optioneel)</Label>
              <Input placeholder="bijv. Groenten leverancier" value={costForm.description} onChange={(e) => setCostForm({...costForm, description: e.target.value})} />
            </div>
            <Button className="w-full bg-rose-600 hover:bg-rose-700 text-white" onClick={handleQuickCost}>
              <Plus className="w-4 h-4 mr-2" />Toevoegen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hours Dialog */}
      <Dialog open={hoursDialogOpen} onOpenChange={setHoursDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Snel Uren Invoeren</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Medewerker</Label>
              <Select value={hoursForm.employee_id} onValueChange={(v) => setHoursForm({...hoursForm, employee_id: v})}>
                <SelectTrigger><SelectValue placeholder="Selecteer medewerker" /></SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Datum</Label>
                <Input type="date" value={hoursForm.date} onChange={(e) => setHoursForm({...hoursForm, date: e.target.value})} />
              </div>
              <div>
                <Label>Uren</Label>
                <Input type="number" placeholder="8" value={hoursForm.hours} onChange={(e) => setHoursForm({...hoursForm, hours: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={hoursForm.type} onValueChange={(v) => setHoursForm({...hoursForm, type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gewerkt">Gewerkt</SelectItem>
                  <SelectItem value="verlof">Verlof</SelectItem>
                  <SelectItem value="ziek">Ziek</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" onClick={handleQuickHours}>
              <Plus className="w-4 h-4 mr-2" />Toevoegen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Review Verzoek Versturen</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Naam gast</Label>
              <Input placeholder="Jan Jansen" value={reviewForm.guest_name} onChange={(e) => setReviewForm({...reviewForm, guest_name: e.target.value})} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" placeholder="jan@email.com" value={reviewForm.email} onChange={(e) => setReviewForm({...reviewForm, email: e.target.value})} />
            </div>
            <div>
              <Label>Telefoon (optioneel)</Label>
              <Input placeholder="+31 6..." value={reviewForm.phone} onChange={(e) => setReviewForm({...reviewForm, phone: e.target.value})} />
            </div>
            <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white" onClick={handleQuickReview}>
              <Star className="w-4 h-4 mr-2" />Verzoek Versturen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Arrangement Dialog */}
      <Dialog open={arrangementDialogOpen} onOpenChange={setArrangementDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Arrangement Boeken</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Arrangement</Label>
              <Select value={arrangementForm.template_id} onValueChange={(v) => setArrangementForm({...arrangementForm, template_id: v})}>
                <SelectTrigger><SelectValue placeholder="Selecteer arrangement" /></SelectTrigger>
                <SelectContent>
                  {arrangementTemplates.map(arr => (
                    <SelectItem key={arr.id} value={arr.id}>{arr.name} - {formatCurrency(arr.price_per_person)}/p.p.</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Naam gast</Label>
              <Input placeholder="Naam van de boeking" value={arrangementForm.guest_name} onChange={(e) => setArrangementForm({...arrangementForm, guest_name: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Aantal personen</Label>
                <Input type="number" placeholder="10" value={arrangementForm.guest_count} onChange={(e) => setArrangementForm({...arrangementForm, guest_count: e.target.value})} />
              </div>
              <div>
                <Label>Datum</Label>
                <Input type="date" value={arrangementForm.date} onChange={(e) => setArrangementForm({...arrangementForm, date: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Tijd</Label>
              <Input type="time" value={arrangementForm.time} onChange={(e) => setArrangementForm({...arrangementForm, time: e.target.value})} />
            </div>
            <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white" onClick={handleQuickArrangement}>
              <Utensils className="w-4 h-4 mr-2" />Boeken
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Event Dialog */}
      <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Event Aanmaken</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Event naam</Label>
              <Input placeholder="bijv. Valentijns Diner" value={eventForm.name} onChange={(e) => setEventForm({...eventForm, name: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Datum</Label>
                <Input type="date" value={eventForm.event_date} onChange={(e) => setEventForm({...eventForm, event_date: e.target.value})} />
              </div>
              <div>
                <Label>Starttijd</Label>
                <Input type="time" value={eventForm.start_time} onChange={(e) => setEventForm({...eventForm, start_time: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Verwachte gasten</Label>
              <Input type="number" placeholder="50" value={eventForm.expected_guests} onChange={(e) => setEventForm({...eventForm, expected_guests: e.target.value})} />
            </div>
            <div>
              <Label>Beschrijving (optioneel)</Label>
              <Textarea placeholder="Korte beschrijving van het event..." value={eventForm.description} onChange={(e) => setEventForm({...eventForm, description: e.target.value})} />
            </div>
            <Button className="w-full bg-pink-600 hover:bg-pink-700 text-white" onClick={handleQuickEvent}>
              <PartyPopper className="w-4 h-4 mr-2" />Event Aanmaken
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Actie Toevoegen</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Titel</Label>
              <Input placeholder="bijv. Menu aanpassen" value={actionForm.title} onChange={(e) => setActionForm({...actionForm, title: e.target.value})} />
            </div>
            <div>
              <Label>Beschrijving</Label>
              <Textarea placeholder="Wat moet er gebeuren?" value={actionForm.description} onChange={(e) => setActionForm({...actionForm, description: e.target.value})} />
            </div>
            <div>
              <Label>Prioriteit</Label>
              <Select value={actionForm.priority} onValueChange={(v) => setActionForm({...actionForm, priority: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">Hoog</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Laag</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white" onClick={handleQuickAction}>
              <AlertTriangle className="w-4 h-4 mr-2" />Actie Toevoegen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Idea Dialog */}
      <Dialog open={ideaDialogOpen} onOpenChange={setIdeaDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Idee Toevoegen aan Inbox</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Titel *</Label>
              <Input placeholder="Kort en krachtig..." value={ideaForm.title} onChange={(e) => setIdeaForm({...ideaForm, title: e.target.value})} />
            </div>
            <div>
              <Label>Beschrijving</Label>
              <Textarea placeholder="Wat viel je op? Wat kun je hiervan leren?" rows={3} value={ideaForm.description} onChange={(e) => setIdeaForm({...ideaForm, description: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categorie</Label>
                <Select value={ideaForm.category} onValueChange={(val) => setIdeaForm({...ideaForm, category: val})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="algemeen">Algemeen</SelectItem>
                    <SelectItem value="menu">Menu</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="interieur">Interieur</SelectItem>
                    <SelectItem value="proces">Proces</SelectItem>
                    <SelectItem value="personeel">Personeel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Prioriteit</Label>
                <Select value={ideaForm.priority} onValueChange={(val) => setIdeaForm({...ideaForm, priority: val})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="laag">Laag</SelectItem>
                    <SelectItem value="normaal">Normaal</SelectItem>
                    <SelectItem value="hoog">Hoog</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button className="w-full bg-cyan-600 hover:bg-cyan-700 text-white" onClick={handleQuickIdea}>
              <Lightbulb className="w-4 h-4 mr-2" />Naar Inbox
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Company Dialog (Research) */}
      <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Bedrijf Toevoegen aan Research</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <div>
              <Label>Naam *</Label>
              <Input placeholder="bijv. Restaurant De Buren" value={companyForm.name} onChange={(e) => setCompanyForm({...companyForm, name: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categorie</Label>
                <Select value={companyForm.category} onValueChange={(v) => setCompanyForm({...companyForm, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="concurrent">Concurrent</SelectItem>
                    <SelectItem value="inspiratie">Inspiratie</SelectItem>
                    <SelectItem value="leverancier">Leverancier</SelectItem>
                    <SelectItem value="partner">Partner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Plaats</Label>
                <Input placeholder="bijv. Amsterdam" value={companyForm.city} onChange={(e) => setCompanyForm({...companyForm, city: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Website</Label>
              <Input placeholder="https://..." value={companyForm.website} onChange={(e) => setCompanyForm({...companyForm, website: e.target.value})} />
            </div>
            
            {/* Social Media Fields */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-sm font-semibold mb-3 block">Social Media</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#57534E]">Instagram</Label>
                  <Input placeholder="@username of URL" value={companyForm.instagram_url} onChange={(e) => setCompanyForm({...companyForm, instagram_url: e.target.value})} />
                </div>
                <div>
                  <Label className="text-xs text-[#57534E]">Facebook</Label>
                  <Input placeholder="URL" value={companyForm.facebook_url} onChange={(e) => setCompanyForm({...companyForm, facebook_url: e.target.value})} />
                </div>
                <div>
                  <Label className="text-xs text-[#57534E]">LinkedIn</Label>
                  <Input placeholder="URL" value={companyForm.linkedin_url} onChange={(e) => setCompanyForm({...companyForm, linkedin_url: e.target.value})} />
                </div>
                <div>
                  <Label className="text-xs text-[#57534E]">TikTok</Label>
                  <Input placeholder="@username of URL" value={companyForm.tiktok_url} onChange={(e) => setCompanyForm({...companyForm, tiktok_url: e.target.value})} />
                </div>
              </div>
            </div>
            
            <div>
              <Label>Notities</Label>
              <Textarea placeholder="Wat wil je onthouden over dit bedrijf?" value={companyForm.notes} onChange={(e) => setCompanyForm({...companyForm, notes: e.target.value})} />
            </div>
            <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white" onClick={handleAddCompany}>
              <Building2 className="w-4 h-4 mr-2" />Toevoegen aan Research
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Shift Dialog */}
      <Dialog open={shiftDialogOpen} onOpenChange={setShiftDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Dienst Inplannen</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Medewerker *</Label>
              <Select value={shiftForm.employee_id} onValueChange={(v) => setShiftForm({...shiftForm, employee_id: v})}>
                <SelectTrigger><SelectValue placeholder="Selecteer medewerker" /></SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Datum</Label>
              <Input type="date" value={shiftForm.date} onChange={(e) => setShiftForm({...shiftForm, date: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Starttijd</Label>
                <Input type="time" value={shiftForm.start_time} onChange={(e) => setShiftForm({...shiftForm, start_time: e.target.value})} />
              </div>
              <div>
                <Label>Eindtijd</Label>
                <Input type="time" value={shiftForm.end_time} onChange={(e) => setShiftForm({...shiftForm, end_time: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Rol/Functie</Label>
              <Input placeholder="bijv. Kok, Bediening, Bar" value={shiftForm.role} onChange={(e) => setShiftForm({...shiftForm, role: e.target.value})} />
            </div>
            <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white" onClick={handleQuickShift}>
              <Users className="w-4 h-4 mr-2" />Dienst Inplannen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Calendar Day Dialog */}
      <Dialog open={calendarDayDialogOpen} onOpenChange={setCalendarDayDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#C2410C]" />
              {selectedCalendarDay && new Date(selectedCalendarDay.dateStr).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}
            </DialogTitle>
          </DialogHeader>
          {selectedCalendarDay && (
            <div className="space-y-4 pt-2">
              {/* Seasons */}
              {selectedCalendarDay.seasons.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-amber-500" /> Seizoensacties
                  </h4>
                  {selectedCalendarDay.seasons.map((season, idx) => (
                    <div key={idx} className="p-2 bg-amber-50 rounded-lg mb-2">
                      <p className="font-medium text-sm">{season.name}</p>
                      {season.suggested_actions && <p className="text-xs text-[#57534E] mt-1">{season.suggested_actions}</p>}
                    </div>
                  ))}
                </div>
              )}

              {/* Events */}
              {selectedCalendarDay.events.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-purple-700 mb-2 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-purple-500" /> Events
                  </h4>
                  {selectedCalendarDay.events.map(event => (
                    <div key={event.id} className="p-2 bg-purple-50 rounded-lg mb-2 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{event.name}</p>
                        <p className="text-xs text-[#57534E]">{event.start_time} • {event.expected_guests || 0} gasten</p>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => { setCalendarDayDialogOpen(false); openEditEvent(event); }}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Campaigns */}
              {selectedCalendarDay.campaigns.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" /> Campagnes
                  </h4>
                  {selectedCalendarDay.campaigns.map(campaign => (
                    <div key={campaign.id} className="p-2 bg-emerald-50 rounded-lg mb-2">
                      <p className="font-medium text-sm">{campaign.name}</p>
                      <p className="text-xs text-[#57534E]">{campaign.goal}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty state */}
              {selectedCalendarDay.seasons.length === 0 && selectedCalendarDay.events.length === 0 && selectedCalendarDay.campaigns.length === 0 && (
                <div className="text-center py-6 text-[#57534E]">
                  <Calendar className="w-10 h-10 mx-auto mb-2 text-stone-300" />
                  <p className="text-sm">Niets gepland voor deze dag</p>
                  <Button variant="link" className="text-[#C2410C]" onClick={() => { setCalendarDayDialogOpen(false); setEventDialogOpen(true); }}>
                    + Event toevoegen
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Event Dialog */}
      <Dialog open={editEventDialogOpen} onOpenChange={setEditEventDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Event Bewerken</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Event naam</Label>
              <Input value={eventForm.name} onChange={(e) => setEventForm({...eventForm, name: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Datum</Label>
                <Input type="date" value={eventForm.event_date} onChange={(e) => setEventForm({...eventForm, event_date: e.target.value})} />
              </div>
              <div>
                <Label>Starttijd</Label>
                <Input type="time" value={eventForm.start_time} onChange={(e) => setEventForm({...eventForm, start_time: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Verwachte gasten</Label>
              <Input type="number" value={eventForm.expected_guests} onChange={(e) => setEventForm({...eventForm, expected_guests: e.target.value})} />
            </div>
            <div>
              <Label>Beschrijving</Label>
              <Textarea value={eventForm.description} onChange={(e) => setEventForm({...eventForm, description: e.target.value})} />
            </div>
            <div className="flex gap-2">
              <Button className="flex-1 bg-pink-600 hover:bg-pink-700 text-white" onClick={handleUpdateEvent}>
                Opslaan
              </Button>
              <Button variant="outline" className="text-rose-600" onClick={() => { handleDeleteEvent(selectedEvent?.id); setEditEventDialogOpen(false); }}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Arrangement Dialog */}
      <Dialog open={editArrangementDialogOpen} onOpenChange={setEditArrangementDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Arrangement Bewerken</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Naam gast</Label>
              <Input value={arrangementForm.guest_name} onChange={(e) => setArrangementForm({...arrangementForm, guest_name: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Aantal personen</Label>
                <Input type="number" value={arrangementForm.guest_count} onChange={(e) => setArrangementForm({...arrangementForm, guest_count: e.target.value})} />
              </div>
              <div>
                <Label>Datum</Label>
                <Input type="date" value={arrangementForm.date} onChange={(e) => setArrangementForm({...arrangementForm, date: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Tijd</Label>
              <Input type="time" value={arrangementForm.time} onChange={(e) => setArrangementForm({...arrangementForm, time: e.target.value})} />
            </div>
            <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white" onClick={handleUpdateArrangement}>
              Opslaan
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Key Required Dialog */}
      <AIKeyRequiredDialog 
        open={showAIKeyDialog} 
        onOpenChange={setShowAIKeyDialog} 
      />
    </div>
  );
};

export default DashboardPage;
